import type {
  CaseMetricsResponse,
  CrmMetricsResponse,
  TicketMetricsResponse,
} from '@chatbot/shared-types';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { ErrorAlert } from '../components/ErrorAlert.tsx';
import { PageHeader } from '../components/PageHeader.tsx';
import { CaseMetricsSection } from '../components/cases/CaseMetricsSection.js';
import {
  ChannelSplit,
  ConversationsByDayChart,
  CrmKpiGrid,
  FunnelChart,
  LiveNowPanel,
  PeakHoursCrmChart,
  UnmetDemandPanel,
} from '../components/metrics/index.ts';
import { TicketMetricsSection } from '../components/tickets/TicketMetricsSection.tsx';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Spinner,
  Tab,
  Tabs,
} from '../components/ui/index.ts';
import { useAsync } from '../hooks/useAsync.ts';
import { api } from '../lib/api.ts';
import { motivoDeVacio } from '../lib/vacio.ts';

// ── Date helpers ──────────────────────────────────────────────────────────────

function toDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function nDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDate(d);
}

type Range = '24h' | '7d' | '30d' | 'custom';

// ── MetricsPage ───────────────────────────────────────────────────────────────

/**
 * Dashboard de CRM conversacional — agnóstico del vertical del tenant.
 * Contrato: GET /admin/metrics/crm?tenantId=&from=&to=
 */
export function MetricsPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [range, setRange] = useState<Range>('30d');
  const [from, setFrom] = useState<string>(nDaysAgo(29));
  const [to, setTo] = useState<string>(toDate(new Date()));

  const { data: tenantsData } = useAsync(() => api.tenants.list());
  const tenants = tenantsData?.tenants ?? [];
  const activeTenantName = tenants.find((t) => t.tenantId === tenantId)?.name;

  function handleTabClick(r: Exclude<Range, 'custom'>) {
    setRange(r);
    setTo(toDate(new Date()));
    if (r === '24h') setFrom(nDaysAgo(1));
    else if (r === '7d') setFrom(nDaysAgo(6));
    else setFrom(nDaysAgo(29));
  }

  const {
    data: metrics,
    status,
    error,
    refetch,
  } = useAsync<CrmMetricsResponse>(() => api.metrics.getCrm(tenantId ?? '', from, to));

  // Métricas de operaciones (ADR-023). Va aparte del CRM a propósito: son capas
  // distintas —el contacto es la persona, el caso es la operación— y una puede
  // no tener datos sin que la otra deje de servir.
  const { data: casos, refetch: refetchCasos } = useAsync<CaseMetricsResponse>(() =>
    api.metrics.getCases(tenantId ?? '', from, to),
  );

  // Soporte (EPIC-V2-TICKETS). Aparte de las de casos por la misma razón: un
  // tenant de agendamiento no tiene tickets, y eso es un resultado correcto —
  // la sección no se dibuja en vez de mostrar ceros.
  const { data: tickets, refetch: refetchTickets } = useAsync<TicketMetricsResponse>(() =>
    api.metrics.getTickets(tenantId ?? '', from, to),
  );

  // useAsync solo re-ejecuta con refetch(); sin esto, cambiar de tenant o de
  // rango dejaría los datos del query anterior en pantalla.
  const isFirstRender = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: tenantId/from/to son triggers intencionales de recarga
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    refetch();
    // Sin esto, cambiar de tenant deja en pantalla el backlog, el embudo y el
    // forecast del ANTERIOR: datos de otro cliente (regla A2). `useAsync` sólo
    // re-ejecuta con `refetch()`, y este efecto era el único que lo llamaba.
    refetchCasos();
    refetchTickets();
  }, [tenantId, from, to, refetch, refetchCasos, refetchTickets]);

  const hasData = !!metrics && metrics.byDay.length > 0;

  return (
    <AppShell>
      <div className="flex flex-col h-full animate-fade-in bg-surface-sunken">
        <PageHeader
          title={`Métricas — ${activeTenantName ?? tenantId ?? '—'}`}
          subtitle={`${from} → ${to}`}
        />

        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/*
           * Cobertura parcial: se pidió una ventana que arranca antes del
           * primer dato. Los totales son reales pero de un período más corto
           * que el rótulo del rango, y presentarlos sin decirlo sería mostrar
           * un número incompleto con cara de total.
           *
           * Va arriba de todo y no al pie de cada tarjeta: afecta a todos los
           * números de la pantalla, no a uno.
           */}
          {metrics?.coverage?.partial ? (
            <div
              data-testid="coverage-warning"
              className="rounded-lg border border-edge bg-surface-sunken px-4 py-3 text-body-sm text-content-secondary"
            >
              {metrics.coverage.from
                ? `Estos números cubren desde el ${new Date(`${metrics.coverage.from}T12:00:00Z`).toLocaleDateString('es-CO', { day: 'numeric', month: 'long' })}, no el rango completo: antes de esa fecha todavía no se registraban.`
                : 'Todavía no hay datos registrados para este tenant en ningún período.'}
            </div>
          ) : null}

          <Card variant="default">
            <CardBody id="metrics-date-range" className="flex gap-4 flex-wrap items-end">
              {tenants.length > 1 && (
                <label className="flex flex-col gap-1">
                  <span className="text-caption font-medium text-content-secondary">Tenant</span>
                  <select
                    id="metrics-tenant-select"
                    data-testid="metrics-tenant-select"
                    value={tenantId ?? ''}
                    onChange={(e) => navigate(`/tenants/${e.target.value}/metrics`)}
                    className="h-9 rounded-lg border border-edge bg-surface-base px-3 text-body-sm text-content-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {tenants.map((t) => (
                      <option key={t.tenantId} value={t.tenantId}>
                        {t.name || t.tenantId}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <Tabs variant="segment">
                <Tab active={range === '24h'} onClick={() => handleTabClick('24h')}>
                  24h
                </Tab>
                <Tab active={range === '7d'} onClick={() => handleTabClick('7d')}>
                  7d
                </Tab>
                <Tab active={range === '30d'} onClick={() => handleTabClick('30d')}>
                  30d
                </Tab>
              </Tabs>

              <Input
                id="metrics-from"
                label="Desde"
                type="date"
                size="sm"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setRange('custom');
                }}
              />
              <Input
                id="metrics-to"
                label="Hasta"
                type="date"
                size="sm"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setRange('custom');
                }}
              />
              <Button id="metrics-refresh" variant="outline" size="sm" onClick={() => refetch()}>
                Actualizar
              </Button>
            </CardBody>
          </Card>

          {status === 'error' && (
            <ErrorAlert message={`No se pudieron cargar las métricas: ${error ?? 'error'}`} />
          )}

          {(status === 'loading' || status === 'idle') && (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" />
            </div>
          )}

          {status === 'success' && metrics && (
            <>
              {metrics.truncated && (
                <p
                  data-testid="metrics-truncated"
                  className="text-caption text-warning-700 dark:text-warning-300"
                >
                  Resultados parciales: el rango es muy amplio y se truncaron algunos datos. Acotá
                  las fechas para ver el total.
                </p>
              )}

              <UnmetDemandPanel
                conversion={metrics.conversion}
                unmetDemand={metrics.unmetDemand ?? []}
                effort={metrics.effort}
              />

              <CrmKpiGrid kpis={metrics.kpis} byDay={metrics.byDay} coverage={metrics.coverage} />

              <Card variant="elevated">
                <CardHeader>
                  <h3 className="text-h5 text-content-primary">Embudo de contactos</h3>
                  <p className="text-caption text-content-tertiary">
                    Cada etapa cuenta a quienes llegaron al menos hasta ahí. Avanza sola con lo que
                    hace cada persona.
                  </p>
                </CardHeader>
                <CardBody>
                  <FunnelChart stages={metrics.funnel ?? []} />
                </CardBody>
              </Card>

              <CaseMetricsSection casos={casos} />

              <TicketMetricsSection tickets={tickets} />

              <LiveNowPanel liveNow={metrics.liveNow} />

              {!hasData ? (
                <Card variant="default" data-testid="metrics-empty">
                  <CardBody>
                    <EmptyState
                      message="No hay conversaciones registradas en el rango seleccionado. Probá con un rango más amplio."
                      action={
                        <Button variant="outline" size="sm" onClick={() => handleTabClick('30d')}>
                          Ver últimos 30 días
                        </Button>
                      }
                    />
                  </CardBody>
                </Card>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <Card variant="elevated" className="lg:col-span-2">
                    <CardHeader>
                      <h3 className="text-h5 text-content-primary">Conversaciones por día</h3>
                      <p className="text-caption text-content-tertiary">
                        Los mensajes del día aparecen en el tooltip
                      </p>
                    </CardHeader>
                    <CardBody>
                      <ConversationsByDayChart
                        data={metrics.byDay}
                        motivoVacio={motivoDeVacio({ from, to, coverage: metrics.coverage })}
                      />
                    </CardBody>
                  </Card>

                  <Card variant="elevated">
                    <CardHeader>
                      <h3 className="text-h5 text-content-primary">Distribución por canal</h3>
                    </CardHeader>
                    <CardBody>
                      <ChannelSplit
                        byChannel={metrics.byChannel}
                        motivoVacio={motivoDeVacio({ from, to, coverage: metrics.coverage })}
                      />
                    </CardBody>
                  </Card>

                  <Card variant="elevated" className="lg:col-span-3">
                    <CardHeader>
                      <h3 className="text-h5 text-content-primary">Horas pico</h3>
                      <p className="text-caption text-content-tertiary">
                        Conversaciones iniciadas por hora del día (0–23)
                      </p>
                    </CardHeader>
                    <CardBody>
                      <PeakHoursCrmChart
                        data={metrics.byHour}
                        motivoVacio={motivoDeVacio({ from, to, coverage: metrics.coverage })}
                      />
                    </CardBody>
                  </Card>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
